import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Send, User, ExternalLink, BookOpen, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import HighlightableText from "@/components/HighlightableText";
import { useAuth } from "@/context/AuthContext";
import SourceDetails from "@/components/SourceDetails";
import FeedbackBar from "@/components/FeedbackBar";
import JurisdictionSelector from "@/components/JurisdictionSelector";
import AiLabel from "@/components/AiLabel";
import ReplyMarkdown from "@/components/ReplyMarkdown";
import EmployerContextSelector from "@/components/EmployerContextSelector";
import ListenButton from "@/components/ListenButton";
import { AttachButton, AttachmentChip, AttachmentBadge, useAttachmentMeta } from "@/components/ChatAttachment";
import { uploadInChunks } from "@/lib/upload";
import { MapPin, Cpu, Building2, AlertTriangle } from "lucide-react";

const HIGH_RISK_NOTICE = "This response provides informational and workflow support only. Review the applicable law, organizational policy, collective bargaining agreement, and relevant facts. Obtain qualified human or legal review before taking employment action.";

export default function Chat() {
  const { user, setUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [srcDialog, setSrcDialog] = useState(null); // document
  const [saveDialog, setSaveDialog] = useState(null); // {text, source_ref, source_title, citation}
  const [note, setNote] = useState("");
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => { // grow with content up to max-height, then scroll internally
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [input]);

  useEffect(() => {
    api.get("/prompts/suggested", { params: { context: user?.employer_context || "unspecified" } }).then((r) => {
      const role = user?.role || "employee";
      setSuggested(r.data.filter((p) => p.role === role).slice(0, 4));
    });
  }, [user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const openCitation = async (docId) => {
    try {
      const { data } = await api.get(`/documents/${docId}`);
      setSrcDialog(data);
    } catch {
      toast.error("Source document unavailable");
    }
  };

  const [jurisdictions, setJurisdictions] = useState(null);
  const attMeta = useAttachmentMeta();
  const [pending, setPending] = useState(null); // {file, status, progress, error}
  const send = async (text, opts = {}) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    const file = pending?.file;
    setInput("");
    setMessages((m) => [...m, { _key: crypto.randomUUID(), role: "user", content: q, attachment: file ? { name: file.name, size: file.size, kind: file.type.startsWith("image/") ? "image" : "document" } : null }]);
    setBusy(true);
    let attachment_id = null;
    try {
      if (file) {
        setPending((p) => ({ ...p, status: "uploading", progress: 0, error: null }));
        const rec = await uploadInChunks(file, { purpose: "chat" }, (pct) => setPending((p) => (p ? { ...p, progress: pct } : p)));
        attachment_id = rec.id;
        setPending((p) => (p ? { ...p, status: "analyzing", progress: 100 } : p));
      }
      const { data } = await api.post("/chat", { message: q, session_id: sessionId, jurisdictions: opts.jurisdictions || jurisdictions || [], employer_context: opts.employer_context, attachment_id });
      setSessionId(data.session_id);
      setPending(null);
      setMessages((m) => [...m, { _key: data.message_id, role: "assistant", id: data.message_id, content: data.reply, sources: data.sources, human_review_required: data.human_review_required, high_risk_categories: data.high_risk_categories || [], high_risk_notice: data.high_risk_notice, jurisdiction_label: data.jurisdiction_label, jurisdiction_notice: data.jurisdiction_notice, model_label: data.model_label, employer_context: data.employer_context, employer_context_label: data.employer_context_label, resources_searched: data.resources_searched, sources_excluded: data.sources_excluded || [], no_source_notice: data.no_source_notice, context_notice: data.context_notice, org_policy_notice: data.org_policy_notice, clarification_notice: data.clarification_notice, fallback_reason: data.fallback_reason, fallback_notice: data.fallback_notice, follow_ups: data.follow_ups || [], official_links: data.official_links || [], comparison_requested: data.comparison_requested, attachment: data.attachment }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail[0]?.msg : null;
      toast.error(msg || "AI error");
      if (file) setPending((p) => (p ? { ...p, status: "error", error: msg || "Upload failed — remove the file or try again" } : p));
      setMessages((m) => [...m, { _key: crypto.randomUUID(), role: "assistant", content: file && msg ? `Your attachment could not be processed: ${msg}` : "Sorry, I could not answer that right now.", sources: [] }]);
    } finally {
      setBusy(false);
    }
  };

  const refine = async (f) => {
    if (busy) return;
    if (f.type === "jurisdiction") { setJurisdictions(f.jurisdictions); send(f.message, { jurisdictions: f.jurisdictions }); return; }
    try { const { data } = await api.patch("/auth/preferences", { employer_context: f.employer_context }); setUser(data); }
    catch { toast.error("Could not save employer type"); }
    send(f.message, { employer_context: f.employer_context });
  };

  const onSaveHighlight = (text, msg) => {
    const primarySrc = msg.sources?.[0];
    setSaveDialog({
      text,
      source_type: "chat",
      source_ref: sessionId || "session",
      source_title: primarySrc?.title || "AI Response",
      citation: primarySrc?.citation || "",
    });
  };

  const confirmSave = async () => {
    try {
      await api.post("/highlights", { ...saveDialog, note });
      toast.success("Highlight saved");
      setSaveDialog(null); setNote("");
    } catch (e) {
      toast.error("Could not save highlight");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">LUMIS HR Assistant</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E]">Ask LUMIS HR anything</h1>
        <p className="text-sm text-slate-600 mt-1" data-testid="chat-grounding-statement">Answers are designed to use your authorized library and provide clickable citations when sources are available.</p>
        <p className="text-xs text-slate-600 mt-1" data-testid="chat-review-verify"><strong className="text-[#0A2A5E]">Review and Verify AI Responses.</strong> AI can make mistakes. Review the information, verify it against your organization's policies and cited sources, and consult HR, legal counsel, or another qualified professional when appropriate.</p>
        <div className="mt-2 flex flex-wrap items-center gap-2"><JurisdictionSelector value={jurisdictions} onChange={setJurisdictions} /><EmployerContextSelector /><AiLabel /></div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-lg flex flex-col h-[calc(100vh-15rem)] min-h-[28rem]">
        <ScrollArea className="flex-1 min-h-0 chat-scroll">
          <div className="p-6 space-y-6">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#0A2A5E] to-[#1E5AB8] flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-white"/>
                </div>
                <h3 className="font-display font-bold text-lg text-[#0A2A5E]">How can I help today?</h3>
                <p className="text-sm text-slate-500 mt-1">Try one of these prompts to get started.</p>
                <div className="grid sm:grid-cols-2 gap-2 mt-6 max-w-2xl mx-auto">
                  {suggested.map((s, i) => (
                    <button
                      key={s.text || s.prompt || i}
                      data-testid={`suggested-prompt-${i}`}
                      onClick={() => send(s.text)}
                      className="text-left p-4 rounded-xl border border-slate-200 bg-slate-50 hover:bg-[#FFF7E8] hover:border-[#F5A623] transition text-sm text-slate-800"
                    >
                      {s.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={m._key || m.id || i} className={`flex gap-3 animate-fade-up ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                  m.role === "user" ? "bg-slate-200" : "bg-gradient-to-br from-[#0A2A5E] to-[#1E5AB8]"
                }`}>
                  {m.role === "user" ? <User className="w-4 h-4 text-slate-700"/> : <Sparkles className="w-4 h-4 text-white"/>}
                </div>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  m.role === "user" ? "bg-[#0A2A5E] text-white" : "bg-[#FFF7E8] border border-[#FBD38D] text-[#0A2A5E]"
                }`}>
                  {m.role === "user" ? (
                    <div className="text-sm whitespace-pre-wrap">{m.content}<AttachmentBadge attachment={m.attachment} light /></div>
                  ) : (
                    <HighlightableText
                      testid={`assistant-message-${i}`}
                      onSave={(t) => onSaveHighlight(t, m)}
                    >
                      {m.employer_context_label && (
                        <div data-testid="response-context" className="mb-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-[#0A2A5E]">
                          <Building2 className="w-3.5 h-3.5 text-[#C7761A]" /><span className="text-slate-500">Employer context:</span><span className="font-bold" data-testid="response-employer-context">{m.employer_context_label}</span>
                          <span className="text-slate-300">|</span><span className="text-slate-500">Resources searched:</span><span className="font-semibold" data-testid="response-resources">{m.resources_searched}</span>
                        </div>
                      )}
                      {m.jurisdiction_label && (
                        <div data-testid="response-jurisdiction" className="mb-2 flex flex-wrap items-center gap-1.5 text-[11px] text-[#0A2A5E]">
                          <MapPin className="w-3.5 h-3.5 text-[#C7761A]" /><span className="text-slate-500">Jurisdictional context:</span><span className="font-bold">{m.jurisdiction_label}</span>
                        </div>
                      )}
                      {m.context_notice && (
                        <div data-testid="context-notice" role="note" className="mb-3 p-2.5 rounded-lg bg-[#F5F9FE] border border-slate-300 text-xs text-slate-700 leading-relaxed">{m.context_notice}</div>
                      )}
                      {m.attachment && (
                        <div data-testid="attachment-analysis" className="mb-3 p-2.5 rounded-lg bg-white border border-[#FBD38D] text-xs text-slate-700 leading-relaxed">
                          <div className="font-semibold text-[#0A2A5E]">Analyzed your attached {m.attachment.kind === "image" ? "image" : "document"}: <span className="font-normal">{m.attachment.filename}</span> <span className="text-slate-400">· private to you, not added to the library</span></div>
                          {m.attachment.read_notes?.length > 0 && (
                            <ul data-testid="attachment-read-notes" className="mt-1.5 space-y-0.5 text-[#7C3F00]">
                              {m.attachment.read_notes.map((n) => <li key={n} className="flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /><span>Could not fully read: {n}</span></li>)}
                            </ul>
                          )}
                        </div>
                      )}
                      {m.no_source_notice && (
                        <div data-testid="no-source-notice" role="note" className="mb-3 p-2.5 rounded-lg bg-[#FFF7E8] border border-[#F5A623] text-xs text-[#7C3F00] leading-relaxed">{m.no_source_notice}</div>
                      )}
                      {m.jurisdiction_notice && (
                        <div data-testid="jurisdiction-notice" role="note" className="mb-3 p-2.5 rounded-lg bg-[#FFF7E8] border border-[#F5A623] text-xs text-[#7C3F00] leading-relaxed">{m.jurisdiction_notice}</div>
                      )}
                      {m.fallback_notice && (
                        <div data-testid="fallback-notice" role="note" className="mb-3 p-2.5 rounded-lg bg-[#FFF7E8] border border-[#F5A623] text-xs text-[#7C3F00] leading-relaxed">{m.fallback_notice}</div>
                      )}
                      {m.org_policy_notice && (
                        <div data-testid="org-policy-notice" role="note" className="mb-3 p-2.5 rounded-lg bg-[#F5F9FE] border border-slate-300 text-xs text-slate-700 leading-relaxed">{m.org_policy_notice}</div>
                      )}
                      {m.clarification_notice && (
                        <div data-testid="clarification-notice" role="note" className="mb-3 p-2.5 rounded-lg bg-[#F5F9FE] border border-slate-300 text-xs text-slate-700 leading-relaxed">{m.clarification_notice}</div>
                      )}
                      {m.human_review_required && (
                        <div data-testid="high-risk-notice" role="alert" className="mb-3 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-900">
                          <div className="flex flex-wrap items-center gap-2">
                            <span data-testid="human-review-badge" className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-rose-700"><ShieldAlert className="w-3.5 h-3.5" /> High-Risk HR Matter</span>
                            {m.high_risk_categories?.map((c) => <span key={c} data-testid="high-risk-category" className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white border border-rose-200 text-rose-700">{c}</span>)}
                          </div>
                          <p className="text-xs leading-relaxed mt-1.5">{m.high_risk_notice || HIGH_RISK_NOTICE}</p>
                        </div>
                      )}
                      <div className="text-sm leading-relaxed">
                        <ReplyMarkdown text={m.content} sources={m.sources || []} onCitationClick={openCitation} />
                      </div>
                      {m.sources_excluded?.length > 0 && (
                        <details data-testid="sources-excluded" className="mt-2 text-[11px] text-slate-500">
                          <summary className="cursor-pointer">{m.sources_excluded.length} source{m.sources_excluded.length > 1 ? "s" : ""} excluded for employer type</summary>
                          <ul className="mt-1 pl-4 list-disc space-y-0.5">{m.sources_excluded.map((e) => <li key={e.doc_id} data-testid={`excluded-${e.doc_id}`}><span className="font-semibold">{e.title}</span> — {e.reason}</li>)}</ul>
                        </details>
                      )}
                      {m.sources?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-[#F5A623] flex flex-wrap gap-1.5">
                          <span className="text-[10px] uppercase tracking-widest font-bold text-[#7C3F00] self-center">Sources:</span>
                          {m.sources.map((s) => (
                            <button
                              key={s.doc_id}
                              onClick={() => openCitation(s.doc_id)}
                              className="citation-badge"
                              data-testid="source-chip"
                            >
                              <BookOpen className="w-3 h-3"/> {s.title}
                            </button>
                          ))}
                        </div>
                      )}
                      {m.sources?.length > 0 ? <SourceDetails sources={m.sources} onOpen={openCitation} /> : m.id && (
                        <div data-testid="source-unavailable" className="mt-3 pt-2 border-t border-[#F5A623] text-xs italic text-slate-500">Source detail not available — independent verification required.</div>
                      )}
                      {m.official_links?.length > 0 && (
                        <div data-testid="official-links" className="mt-3 pt-3 border-t border-[#FBD38D]">
                          <div className="text-[10px] uppercase tracking-widest font-bold text-[#7C3F00] mb-1">Official links · not searchable in LUMIS HR</div>
                          <p className="text-[11px] text-slate-500 mb-1.5">Official link only — source text is not currently searchable in LUMIS HR. Open the official page for current requirements.</p>
                          <ul className="space-y-1">
                            {m.official_links.map((l) => (
                              <li key={l.id}><a href={l.source_url} target="_blank" rel="noopener noreferrer" data-testid={`official-link-${l.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0A2A5E] hover:underline"><ExternalLink className="w-3 h-3" /> {l.title}{l.jurisdiction ? <span className="text-slate-500 font-normal">· {l.jurisdiction}</span> : null}</a></li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {m.follow_ups?.length > 0 && (
                        <div data-testid="follow-ups" className="mt-3 pt-3 border-t border-[#FBD38D]">
                          <div className="text-[10px] uppercase tracking-widest font-bold text-[#7C3F00] mb-1.5">Refine this answer</div>
                          <div className="flex flex-wrap gap-1.5">
                            {m.follow_ups.map((f) => (
                              <button key={f.id} type="button" data-testid={`follow-up-${f.id}`} disabled={busy} onClick={() => refine(f)}
                                className="inline-flex items-center gap-1 rounded-full border border-[#0A2A5E]/30 bg-white px-3 py-1 text-xs font-semibold text-[#0A2A5E] transition-colors hover:bg-[#0A2A5E] hover:text-white disabled:opacity-50">
                                {f.type === "jurisdiction" ? <MapPin className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}{f.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {m.id && (
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                          <ListenButton messageId={m.id} />
                          {m.model_label && <span data-testid="response-model" title={m.model_label} className="inline-flex items-center gap-1 text-[10px] text-slate-500"><Cpu className="w-3 h-3" /> {m.fallback_reason === "no_source" ? "No verified source matched" : "Source-guided answer"}</span>}
                        </div>
                      )}
                      {m.id && <FeedbackBar responseId={m.id} />}
                    </HighlightableText>
                  )}
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex gap-3 animate-fade-up">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0A2A5E] to-[#1E5AB8] flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white"/>
                </div>
                <div className="bg-[#FFF7E8] border border-[#FBD38D] rounded-2xl px-4 py-3 flex items-center gap-2 text-slate-600 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin"/> Consulting the library…
                </div>
              </div>
            )}
            <div ref={bottomRef}/>
          </div>
        </ScrollArea>

        <div className="border-t border-slate-200 p-3 sm:p-4 bg-white space-y-2 shrink-0">
          {pending && (
            <AttachmentChip file={pending.file} status={pending.status} progress={pending.progress} error={pending.error} disabled={busy} onRemove={() => setPending(null)} />
          )}
          <div className="flex gap-2 items-end">
            <AttachButton disabled={busy || !!pending} maxMb={attMeta.max_mb} onPick={(file) => setPending({ file, status: "ready", progress: 0, error: null })} />
            <Textarea
              ref={inputRef}
              data-testid="chat-input"
              value={input}
              rows={3}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
              placeholder={pending ? "Ask a question about your attachment…" : "Ask about FMLA, FLSA, EEOC, telework, LUMIS AI framework…"}
              aria-label="Your question"
              className="flex-1 border-slate-300 resize-none min-h-[64px] sm:min-h-[84px] max-h-[220px] overflow-y-auto leading-relaxed"
              disabled={busy}
            />
            <Button
              data-testid="chat-send-btn"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              aria-label="Send question"
              className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white"
            >
              <Send className="w-4 h-4"/>
            </Button>
          </div>
          <p className="text-[10px] text-slate-400" data-testid="chat-input-hint">Enter to send · Shift+Enter for a new line</p>
          <div data-testid="chat-attachment-help" className="text-[11px] text-slate-500 leading-relaxed">
            <span data-testid="chat-attachment-formats">Attachments: {attMeta.label}.</span>{" "}
            <span data-testid="chat-attachment-warning" role="note" className="text-[#7C3F00] font-medium">{attMeta.warning}</span>
          </div>
        </div>
        <div data-testid="chat-disclaimer" role="note" className="shrink-0 rounded-b-2xl bg-[#FFF7E8] border-t border-[#F5A623] px-4 py-2.5 flex items-start gap-2 text-xs text-[#7C3F00] leading-relaxed">
          <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span><strong>Disclaimer:</strong> LUMIS HR and its reference materials are not legal counsel. LUMIS HR AI-generated guidance is for educational and workflow support. Always verify citations and cross-reference with legal counsel before executing disciplinary actions, layoffs, or policy changes.</span>
        </div>
      </div>

      {/* Source dialog */}
      <Dialog open={!!srcDialog} onOpenChange={(v) => !v && setSrcDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display">{srcDialog?.title}</DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              <span className="text-xs px-2 py-0.5 bg-[#FDECD0] text-[#7C3F00] rounded-full font-semibold">{srcDialog?.citation}</span>
              {srcDialog?.source_url && (
                <a href={srcDialog.source_url} target="_blank" rel="noreferrer" className="text-xs text-[#0A2A5E] hover:underline inline-flex items-center gap-1">
                  Official source <ExternalLink className="w-3 h-3"/>
                </a>
              )}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 mt-2">
            <HighlightableText
              testid="doc-content"
              onSave={(t) => setSaveDialog({
                text: t,
                source_type: "document",
                source_ref: srcDialog?.id,
                source_title: srcDialog?.title,
                citation: srcDialog?.citation,
              })}
            >
              <pre className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 font-sans">{srcDialog?.content}</pre>
            </HighlightableText>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Save highlight dialog */}
      <Dialog open={!!saveDialog} onOpenChange={(v) => !v && (setSaveDialog(null), setNote(""))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Save highlight</DialogTitle>
            <DialogDescription>From: <span className="font-semibold text-slate-800">{saveDialog?.source_title}</span></DialogDescription>
          </DialogHeader>
          <div className="p-3 rounded-lg bg-[#FFF7E8] border border-[#F5A623] text-sm text-slate-800 italic">
            "{saveDialog?.text}"
          </div>
          <Textarea
            data-testid="highlight-note"
            placeholder="Add a personal note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setSaveDialog(null); setNote(""); }}>Cancel</Button>
            <Button data-testid="highlight-save-confirm" onClick={confirmSave} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
